import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { countryToIso } from './utils';
import { log } from '../../lib/logger';
import { clearActiveDraftOrderId } from '../../lib/activeDraftOrder';
import { trackEvent } from '../../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bundles the multi-step Place-Order submission:
 *
 *   0. Payment gate — when `billingMode === 'pay_now'`, Converge auth
 *      must have already happened (`paymentStatus` in
 *      `authorized | captured`). If not, the hook short-circuits with
 *      a sonner toast and scrolls the Pay-with-Card block into view
 *      so the customer can click it. `invoice` mode skips this step
 *      entirely — Net-30 customers never touch a card.
 *   1. (Best-effort) persist the Freightcom quote onto the order — so the
 *      admin can recall the rate later. Capped at 10 s so a stalled
 *      network can never hold up checkout.
 *   2. POST /api/enhanced-orders/{order_id}/submit with any artwork
 *      instructions (either freshly typed here, or from the server-side
 *      draft written by Page 2 — no sessionStorage bridge).
 *   3. Navigate to /order-confirmation.
 *
 * Exposes `{submitting, handleSubmitOrder({proofApproved})}`. The hook
 * owns no product/order state — everything is passed in, which keeps
 * the ReviewOrderPage component dumber.
 */
export function useReviewSubmit({
  orderId,
  order,
  selectedRate,
  shippingRates,
  specialInstructions,
  navigate,
  // Payment-gate inputs. Default values keep older callers (and the
  // server-side validation) authoritative — the hook is purely a
  // friendlier client-side preview of the same rule the backend
  // enforces in `/submit`.
  billingMode = 'pay_now',
  paymentStatus = '',
  // Admin bypass — staff users (`is_admin=true`) can submit orders
  // without going through Converge auth OR Net-30 invoice mode.
  // Mirrors the backend's `is_admin` short-circuit in /submit.
  isAdmin = false,
}) {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitOrder = useCallback(
    async ({ proofApproved = false } = {}) => {
      // Phase 0 — payment gate (client-side preview of the backend
      // rule). Admins bypass entirely. Net-30 customers skip this;
      // pay_now customers must have already authorized via the
      // Converge "Pay with Card" button. We still rely on the backend
      // 402 path as the source of truth in case the user opens the
      // page in a stale tab.
      const mode = (billingMode || 'pay_now').toLowerCase();
      const status = (paymentStatus || '').toLowerCase();
      const paid = status === 'authorized' || status === 'captured';
      if (!isAdmin && mode !== 'invoice' && !paid) {
        toast.error(
          "Please complete the 'Pay with Card' step before submitting your order.",
          {
            description:
              'We authorize the card now and only capture funds once your proof is approved.',
            duration: 6000,
          },
        );
        // Scroll the Pay block into view so the customer sees the CTA
        // they need to click. `data-testid="review-payment-block"` is
        // wrapped around the ConvergePayButton in CostSummaryCard.
        if (typeof document !== 'undefined') {
          const payBlock = document.querySelector(
            '[data-testid="review-payment-block"]',
          );
          if (payBlock) {
            payBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        return;
      }

      setSubmitting(true);
      try {
        // iter114s.91 — Build the freightcom_quote payload here so we
        // can both (a) call /shipping/save-quote (legacy belt) AND
        // (b) include the same shape in the /submit body (atomic
        // suspenders — backend writes it in lockstep with the status
        // bump). The legacy "best-effort save-quote with silent catch"
        // pattern lost the selected rate when save-quote 4xx'd or
        // timed out, leaving customers with Order Confirmation PDFs
        // that showed `Shipping: TBD` even though they'd picked a
        // rate at checkout. The atomic path on the backend makes that
        // failure mode impossible.
        let quotePayload = null;
        if (selectedRate && shippingRates.package) {
          const info = order.shipping_info || {};
          const country = countryToIso(info.country);
          quotePayload = {
            ship_to: {
              street: info.address_line1 || 'Address',
              city: info.city || '',
              province: info.state_province || '',
              postal_code: info.postal_code || '',
              country,
              residential: true,
            },
            package: shippingRates.package,
            selected_rate: selectedRate,
          };
          // Phase 1 — call the legacy save-quote endpoint too. Now
          // truly best-effort because the atomic backup runs server-
          // side; we log but never block submit on this.
          try {
            await Promise.race([
              shippingRates.saveQuote({
                orderId,
                orderNumber: order.order_number,
                shipTo: quotePayload.ship_to,
                package: quotePayload.package,
                selectedRate,
              }),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('save-quote timeout')), 10000),
              ),
            ]);
          } catch (saveErr) {
            log.error('Legacy save-quote failed (backend atomic save will cover this):', saveErr);
          }
        }

        // Phase 2 — submit the order itself.
        const qs = proofApproved ? '?proof_approved=true' : '';
        // Prefer the user's current ReviewOrderPage input; fall back to
        // the server-persisted draft notes that Page 2 wrote straight
        // onto the order doc (no sessionStorage bridge anymore).
        let finalArtworkInstructions = specialInstructions;
        if (!specialInstructions.trim() && order?.artwork_instructions_draft) {
          finalArtworkInstructions = order.artwork_instructions_draft;
        }
        await axios.post(
          `${API_URL}/api/enhanced-orders/${orderId}/submit${qs}`,
          {
            artwork_instructions: finalArtworkInstructions,
            skip_artwork: !order.artwork_files || order.artwork_files.length === 0,
            // iter114s.91 — atomic save: backend persists this in the
            // same Mongo update as the status bump so the resulting
            // Order Confirmation PDF always reflects the rate the
            // customer chose. Null when no rate was selected (allowed
            // for admin sample-runs / customer-service back-fills).
            freightcom_quote: quotePayload,
          },
        );

        // Order is no longer a draft — clear the persistent cart pointer
        // so the next "Add new item" click starts a fresh order.
        clearActiveDraftOrderId();

        // GA4 conversion events. `purchase` is the GA-standard
        // ecommerce event (shows in Monetization reports);
        // `order_placed` is the operator-friendly alias. Mark only ONE
        // as a key event in GA to avoid double-counting conversions.
        const gaParams = {
          transaction_id: order?.order_number || orderId,
          value: Number(order?.total_price || 0),
          currency: 'CAD',
        };
        trackEvent('purchase', gaParams);
        trackEvent('order_placed', gaParams);

        // Phase 3 — hand off to the Thank-You page.
        navigate('/order-confirmation', {
          state: { orderId, orderNumber: order.order_number, proofApproved },
        });
      } catch (error) {
        // Surface the real backend detail when available so customers
        // know WHY submit failed (e.g. "shipping rate expired, refresh").
        // 402 Payment Required is the backend's authoritative rule —
        // mirror the same scroll-to-Pay UX used in the client gate so
        // stale-tab users still get the right hint instead of a raw
        // 402 alert.
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail || error?.message;
        if (status === 402) {
          toast.error(
            detail || "Please complete the 'Pay with Card' step before submitting.",
            { duration: 6000 },
          );
          if (typeof document !== 'undefined') {
            const payBlock = document.querySelector(
              '[data-testid="review-payment-block"]',
            );
            if (payBlock) {
              payBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        } else {
          alert(
            detail
              ? `Failed to submit order: ${detail}`
              : 'Failed to submit order. Please try again.',
          );
        }
        setSubmitting(false);
      }
    },
    [orderId, order, selectedRate, shippingRates, specialInstructions, navigate, billingMode, paymentStatus, isAdmin],
  );

  return { submitting, handleSubmitOrder };
}
